import { useCallback, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import { IconButton, Menu, MenuItem, Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useNotification } from "@context/NotificationContext";
import type { Company } from "@features/port-security/components/SecurityClearence.types.ts";
import UsageDrawer, {
  toUtcDateTime,
  type UsageDrawerSubmitValues,
} from "@features/terminal-services/details/components/UsageDrawer";
import ApproveTerminalServiceDrawer, {
  type TerminalServiceApproveDraft,
} from "@features/terminal-services/details/flow/ApproveTerminalServiceDrawer";
import CancelTerminalServiceDrawer, {
  type TerminalServiceCancelDraft,
} from "@features/terminal-services/details/flow/CancelTerminalServiceDrawer";
import CloseBypassServiceDrawer from "@features/terminal-services/details/flow/CloseBypassServiceDrawer";
import RejectTerminalServiceDrawer, {
  type TerminalServiceRejectDraft,
} from "@features/terminal-services/details/flow/RejectTerminalServiceDrawer";
import { getTerminalServiceFlowActionAvailability } from "@features/terminal-services/details/flow/utils";
import { getTerminalServiceStatusBadgeType } from "@features/terminal-services/details/utils";
import { canCancelTerminalService } from "@features/terminal-services/details/utils/cancellation";
import { getTerminalServiceErrorMessage } from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import {
  FottServiceRequestStatus,
  FottServiceType,
  type CloseBypassServiceRequestPayload,
  type FottServiceResponse,
  type FottServiceType as FottServiceTypeValue,
  type TerminalServiceDetailsResponse,
  type TerminalServiceDocument,
  type TerminalServiceUsageEntry,
} from "@services/terminal-services/types";
import {
  approveTerminalServiceById,
  cancelTerminalServiceById,
  claimTerminalServiceById,
  closeBypassServiceById,
  getTerminalServiceById,
  rejectTerminalServiceById,
  searchFottServices,
  submitTerminalServiceUsageHours,
  unclaimTerminalServiceById,
} from "@services/terminalServicesApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { convertUtcToUae, formatDateToDDMMYYYY } from "@utils/date-time.tsx";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { OTHER_COMPANY_VALUE } from "../request/constants.ts";
import {
  buildFottServicesFilterBody,
  buildFottServicesFilterFields,
  FOTT_SERVICES_COLUMNS,
} from "./utils.tsx";

const LANG_BASE = "terminal-services.fott-services";
const LANG_BASE_HEADER = `${LANG_BASE}.tableHeaders`;

const TEST_ID_BASE = "fott-services";

const LOCAL_STORAGE_PAGE_KEY = "fott-services-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "fott-services-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "fott-services-filters";

const DEFAULT_PAGE_SIZE = 10;
const EMPTY_USER_ACTION_PERMISSIONS: string[] = [];
const FOTT_USER_ROLE = "fott-user";

type FottServicesGridRow = {
  id: string;
  detailsNavigationParam: string;
  fottServiceType: FottServiceTypeValue;
  serviceId: number;
  serviceType: string;
  taskId?: string | null;
  taskStatus?: string | null;
  taskClaimOwnerName?: string | null;
  bookingRequestedFrom: string;
  bookingRequestedTo: string;
  onBehalf: string;
  chargeableParty: string;
  numberOfUsageHours: number | null;
  status: string;
  rawStatus: FottServiceRequestStatus | string;
  badgeType?: "success" | "warning" | "error" | "neutral";
  actions?: ReactNode;
};

type FottServicesActionsCellProps = {
  id: string;
  serviceType: FottServiceTypeValue;
  isClaiming: boolean;
  isUnclaiming: boolean;
  canAddUsage: boolean;
  canCancel: boolean;
  actionAvailability: {
    canClaim: boolean;
    canUnclaim: boolean;
    canApprove: boolean;
    canReject: boolean;
  };
  labels: {
    claim: string;
    unclaim: string;
    approve: string;
    reject: string;
    addUsage: string;
    cancel: string;
    viewCustomers: string;
    downloadPlda: string;
  };
  onClaim: (params: { id: string; serviceType: FottServiceTypeValue }) => void;
  onUnclaim: (params: { id: string; serviceType: FottServiceTypeValue }) => void;
  onApprove: (params: { id: string; serviceType: FottServiceTypeValue }) => void;
  onReject: (params: { id: string; serviceType: FottServiceTypeValue }) => void;
  onAddUsage: (params: { id: string; serviceType: FottServiceTypeValue }) => void;
  onCancelService: (params: { id: string; serviceType: FottServiceTypeValue }) => void;
};

const FottServicesActionsCell = ({
  id,
  serviceType,
  isClaiming,
  isUnclaiming,
  canAddUsage,
  canCancel,
  actionAvailability,
  labels,
  onClaim,
  onUnclaim,
  onApprove,
  onReject,
  onAddUsage,
  onCancelService,
}: FottServicesActionsCellProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const stopCellEvent = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    setAnchorEl(event.currentTarget);
  };

  const handleClaim = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    onClaim({ id, serviceType });
    closeMenu();
  };

  const handleUnclaim = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    onUnclaim({ id, serviceType });
    closeMenu();
  };

  const handleReject = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    onReject({ id, serviceType });
    closeMenu();
  };

  const handleApprove = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    onApprove({ id, serviceType });
    closeMenu();
  };

  const handleAddUsage = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    onAddUsage({ id, serviceType });
    closeMenu();
  };

  const handleCancelService = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    onCancelService({ id, serviceType });
    closeMenu();
  };

  const handlePlaceholderAction = (event: MouseEvent<HTMLElement>) => {
    stopCellEvent(event);
    closeMenu();
  };

  return (
    <>
      <IconButton
        aria-label="More actions"
        size="small"
        data-testid={`${TEST_ID_BASE}-actions-menu-${id}`}
        onClick={openMenu}
        onMouseDown={stopCellEvent}
      >
        <MoreVertOutlinedIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        onClick={stopCellEvent}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {actionAvailability.canClaim && (
          <MenuItem
            data-testid={`${TEST_ID_BASE}-action-claim-${id}`}
            disabled={isClaiming}
            onClick={handleClaim}
          >
            {labels.claim}
          </MenuItem>
        )}
        {actionAvailability.canUnclaim && (
          <MenuItem
            data-testid={`${TEST_ID_BASE}-action-unclaim-${id}`}
            disabled={isUnclaiming}
            onClick={handleUnclaim}
          >
            {labels.unclaim}
          </MenuItem>
        )}
        {actionAvailability.canApprove && (
          <MenuItem data-testid={`${TEST_ID_BASE}-action-approve-${id}`} onClick={handleApprove}>
            {labels.approve}
          </MenuItem>
        )}
        {actionAvailability.canReject && (
          <MenuItem data-testid={`${TEST_ID_BASE}-action-reject-${id}`} onClick={handleReject}>
            {labels.reject}
          </MenuItem>
        )}
        {canAddUsage && (
          <MenuItem data-testid={`${TEST_ID_BASE}-action-add-usage-${id}`} onClick={handleAddUsage}>
            {labels.addUsage}
          </MenuItem>
        )}
        {canCancel && (
          <MenuItem
            data-testid={`${TEST_ID_BASE}-action-cancel-${id}`}
            onClick={handleCancelService}
          >
            {labels.cancel}
          </MenuItem>
        )}
        <MenuItem
          data-testid={`${TEST_ID_BASE}-action-view-customers-${id}`}
          onClick={handlePlaceholderAction}
        >
          {labels.viewCustomers}
        </MenuItem>
        <MenuItem
          data-testid={`${TEST_ID_BASE}-action-download-plda-${id}`}
          onClick={handlePlaceholderAction}
        >
          {labels.downloadPlda}
        </MenuItem>
      </Menu>
    </>
  );
};

const buildDetailsNavigationParam = (id: string, serviceType?: string) => {
  const serviceTypeParam = serviceType ? `?serviceType=${encodeURIComponent(serviceType)}` : "";

  return `${id}${serviceTypeParam}`;
};

const readStoredFilters = () => {
  const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);

  if (!saved) {
    return [];
  }

  try {
    return JSON.parse(saved) as FilterItem[];
  } catch {
    return [];
  }
};

export const FottServicesGrid = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isCustomer = user?.roles.includes("customer") ?? false;
  const isFottUser = user?.roles.includes(FOTT_USER_ROLE) ?? false;
  const shouldShowFottActions = isFottUser && !isCustomer;
  const [rejectDrawerInfo, setRejectDrawerInfo] = useState<{
    id: string;
    serviceType: FottServiceTypeValue;
  } | null>(null);
  const [approveDrawerInfo, setApproveDrawerInfo] = useState<{
    id: string;
    serviceType: FottServiceTypeValue;
  } | null>(null);
  const [closeDrawerInfo, setCloseDrawerInfo] = useState<{
    id: string;
    serviceType: FottServiceTypeValue;
  } | null>(null);
  const [usageDrawerInfo, setUsageDrawerInfo] = useState<{
    id: string;
    serviceType: FottServiceTypeValue;
  } | null>(null);
  const [cancelDrawerInfo, setCancelDrawerInfo] = useState<{
    id: string;
    serviceType: FottServiceTypeValue;
  } | null>(null);
  const [rejectDrafts, setRejectDrafts] = useState<Record<string, TerminalServiceRejectDraft>>({});
  const [cancelDrafts, setCancelDrafts] = useState<Record<string, TerminalServiceCancelDraft>>({});
  const [approveDrafts, setApproveDrafts] = useState<Record<string, TerminalServiceApproveDraft>>(
    {},
  );
  const [approveDrawerResetVersion, setApproveDrawerResetVersion] = useState(0);
  const [rejectDrawerResetVersion, setRejectDrawerResetVersion] = useState(0);
  const [closeDrawerResetVersion, setCloseDrawerResetVersion] = useState(0);
  const [cancelDrawerResetVersion, setCancelDrawerResetVersion] = useState(0);
  const [optimisticRenderedStatus, setOptimisticRenderedStatus] = useState<
    Record<string, FottServiceRequestStatus>
  >({});
  const applyOptimisticRenderedStatus = useCallback(
    (id: string, status: FottServiceRequestStatus) => {
      setOptimisticRenderedStatus((currentStatuses) => ({
        ...currentStatuses,
        [id]: status,
      }));
    },
    [],
  );

  const userActionPermissions = useAuthStore(
    (state) => state.userActionPermissions ?? EMPTY_USER_ACTION_PERMISSIONS,
  );

  const canRequestFottService =
    isCustomer && checkPermission(userActionPermissions, "canRequestFottService");

  const claimTerminalServiceMutation = useMutation({
    mutationFn: ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) =>
      claimTerminalServiceById({
        id,
        serviceType,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });

      notify({
        title: t(`${LANG_BASE}.notifications.claim-success-title`),
        type: "success",
      });
    },
    onError: (claimError) => {
      notify({
        title: t("common.error"),
        description: getTerminalServiceErrorMessage(claimError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const unclaimTerminalServiceMutation = useMutation({
    mutationFn: ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) =>
      unclaimTerminalServiceById({
        id,
        serviceType,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });

      notify({
        title: t(`${LANG_BASE}.notifications.unclaim-success-title`),
        type: "success",
      });
    },
    onError: (unclaimError) => {
      notify({
        title: t("common.error"),
        description:
          getTerminalServiceErrorMessage(unclaimError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const rejectTerminalServiceMutation = useMutation({
    mutationFn: rejectTerminalServiceById,
    onSuccess: async (_, variables) => {
      applyOptimisticRenderedStatus(variables.id, variables.payload.status);

      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });

      setRejectDrawerInfo(null);
      setRejectDrawerResetVersion((version) => version + 1);
      setRejectDrafts((prevDrafts) => {
        const nextDrafts = { ...prevDrafts };
        delete nextDrafts[variables.id];
        return nextDrafts;
      });

      notify({
        title: t(`${LANG_BASE}.notifications.reject-success-title`, {
          defaultValue: "You have rejected the FOTT Service request.",
        }),
        type: "success",
      });
    },
    onError: (rejectError) => {
      setRejectDrawerInfo(null);
      notify({
        title: t("common.error"),
        description:
          getTerminalServiceErrorMessage(rejectError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const approveTerminalServiceMutation = useMutation({
    mutationFn: approveTerminalServiceById,
    onSuccess: async (_, variables) => {
      applyOptimisticRenderedStatus(variables.id, variables.payload.status);
      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });
      setApproveDrawerInfo(null);
      setApproveDrawerResetVersion((version) => version + 1);
      setApproveDrafts((current) => {
        const next = { ...current };
        delete next[variables.id];
        return next;
      });
      notify({
        title: t(`${LANG_BASE}.notifications.approve-success-title`),
        type: "success",
      });
    },
    onError: (approveError) => {
      setApproveDrawerInfo(null);
      notify({
        title: t("common.error"),
        description:
          getTerminalServiceErrorMessage(approveError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const cancelTerminalServiceMutation = useMutation({
    mutationFn: cancelTerminalServiceById,
    onSuccess: async (_, variables) => {
      applyOptimisticRenderedStatus(variables.id, FottServiceRequestStatus.CANCELLED);

      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });
      await queryClient.invalidateQueries({
        queryKey: ["terminal-service-details", variables.id, variables.serviceType],
      });

      setCancelDrawerInfo(null);
      setCancelDrawerResetVersion((version) => version + 1);
      setCancelDrafts((current) => {
        const next = { ...current };
        delete next[variables.id];
        return next;
      });

      notify({
        title: t(`${LANG_BASE}.notifications.cancel-success-title`, {
          defaultValue: "You have canceled this service.",
        }),
        type: "success",
      });
    },
    onError: (cancelError) => {
      setCancelDrawerInfo(null);
      notify({
        title: t("common.error"),
        description:
          getTerminalServiceErrorMessage(cancelError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const closeBypassServiceMutation = useMutation({
    mutationFn: closeBypassServiceById,
    onSuccess: async (_, variables) => {
      applyOptimisticRenderedStatus(variables.id, FottServiceRequestStatus.CLOSED);
      setCloseDrawerInfo(null);
      setCloseDrawerResetVersion((version) => version + 1);

      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });
      await queryClient.invalidateQueries({
        queryKey: ["terminal-service-details", variables.id, FottServiceType.BYPASS_SERVICE],
      });

      notify({
        title: t("terminal-services.details.notifications.close-success-title"),
        type: "success",
      });
    },
    onError: (closeError) => {
      notify({
        title: t("common.error"),
        description: getTerminalServiceErrorMessage(closeError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const submitUsageMutation = useMutation({
    mutationFn: submitTerminalServiceUsageHours,
    onSuccess: async (_, variables) => {
      setUsageDrawerInfo(null);

      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });
      await queryClient.invalidateQueries({
        queryKey: ["terminal-service-details", variables.id, variables.serviceType],
      });

      notify({
        title: t("terminal-services.details.usage.usage-added-toast", {
          defaultValue: "You have added a usage time for this service.",
        }),
        type: "success",
      });
    },
    onError: (usageError) => {
      notify({
        title: t("common.error"),
        description: getTerminalServiceErrorMessage(usageError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const [filters, setFilters] = useState<FilterItem[]>(() => readStoredFilters());

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_PAGE_SIZE;
  });

  const { options: companies = [], isLoading: isLoadingCompanies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isVendor=true`,
    "bff-terminal-service",
  );

  const companyOptions = useMemo(
    () => [
      ...companies.map(({ companyCode, companyNameEnglish }) => ({
        value: companyCode,
        label: companyNameEnglish,
      })),
      {
        value: OTHER_COMPANY_VALUE,
        label: t("terminal-services.OTHER"),
      },
    ],
    [companies, t],
  );

  const {
    data,
    isLoading: isFottServicesLoading,
    error,
  } = useQuery<FottServiceResponse>({
    queryKey: ["fott-services", page, pageSize, filters],
    queryFn: () =>
      searchFottServices({
        page: page - 1,
        pageSize,
        body: buildFottServicesFilterBody(filters),
      }),
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  const { data: usageDrawerDetails } = useQuery<TerminalServiceDetailsResponse>({
    queryKey: ["terminal-service-details", usageDrawerInfo?.id, usageDrawerInfo?.serviceType],
    queryFn: () =>
      getTerminalServiceById({
        id: usageDrawerInfo!.id,
        serviceType: usageDrawerInfo!.serviceType,
      }),
    enabled: Boolean(usageDrawerInfo?.id && usageDrawerInfo?.serviceType),
    staleTime: 0,
  });
  const handleClaimTerminalService = useCallback(
    ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) => {
      claimTerminalServiceMutation.mutate({
        id,
        serviceType,
      });
    },
    [claimTerminalServiceMutation],
  );

  const handleUnclaimTerminalService = useCallback(
    ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) => {
      unclaimTerminalServiceMutation.mutate({
        id,
        serviceType,
      });
    },
    [unclaimTerminalServiceMutation],
  );

  const handleOpenRejectDrawer = useCallback(
    ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) => {
      setRejectDrawerInfo({ id, serviceType });
    },
    [],
  );
  const handleOpenApproveDrawer = useCallback(
    ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) =>
      setApproveDrawerInfo({ id, serviceType }),
    [],
  );
  const handleOpenUsageDrawer = useCallback(
    ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) =>
      setUsageDrawerInfo({ id, serviceType }),
    [],
  );
  const handleOpenCancelDrawer = useCallback(
    ({ id, serviceType }: { id: string; serviceType: FottServiceTypeValue }) =>
      setCancelDrawerInfo({ id, serviceType }),
    [],
  );

  const handleRejectDraftChange = useCallback(
    (draft: TerminalServiceRejectDraft) => {
      if (!rejectDrawerInfo?.id) {
        return;
      }

      setRejectDrafts((prevDrafts) => ({
        ...prevDrafts,
        [rejectDrawerInfo.id]: draft,
      }));
    },
    [rejectDrawerInfo?.id],
  );

  const handleCloseRejectDrawer = useCallback(
    ({ resetDraft = false }: { resetDraft?: boolean } = {}) => {
      const requestId = rejectDrawerInfo?.id;

      setRejectDrawerInfo(null);

      if (resetDraft && requestId) {
        setRejectDrafts((prevDrafts) => {
          const nextDrafts = { ...prevDrafts };
          delete nextDrafts[requestId];
          return nextDrafts;
        });
      }
    },
    [rejectDrawerInfo?.id],
  );

  const handleCancelDraftChange = useCallback(
    (draft: TerminalServiceCancelDraft) => {
      if (!cancelDrawerInfo?.id) return;
      setCancelDrafts((current) => ({ ...current, [cancelDrawerInfo.id]: draft }));
    },
    [cancelDrawerInfo?.id],
  );

  const handleCloseCancelDrawer = useCallback(
    ({ resetDraft = false }: { resetDraft?: boolean } = {}) => {
      const requestId = cancelDrawerInfo?.id;
      setCancelDrawerInfo(null);
      if (resetDraft && requestId) {
        setCancelDrafts((current) => {
          const next = { ...current };
          delete next[requestId];
          return next;
        });
      }
    },
    [cancelDrawerInfo?.id],
  );

  const handleRejectTerminalService = useCallback(
    ({
      notesToCustomer,
      attachment,
    }: {
      notesToCustomer: string;
      attachment: TerminalServiceDocument | null;
    }) => {
      if (!rejectDrawerInfo) {
        return;
      }

      rejectTerminalServiceMutation.mutate({
        id: rejectDrawerInfo.id,
        serviceType: rejectDrawerInfo.serviceType,
        payload: {
          status: FottServiceRequestStatus.REJECTED,
          rejectionNotes: notesToCustomer,
          rejectionDocument: attachment,
          createdBy: user?.name ?? user?.email ?? user?.preferredUsername ?? "",
          createdByRole: user?.roles?.[0] ?? "",
          createdAt: new Date().toISOString(),
        },
      });
    },
    [rejectDrawerInfo, rejectTerminalServiceMutation, user],
  );

  const handleApproveDraftChange = useCallback(
    (draft: TerminalServiceApproveDraft) => {
      if (!approveDrawerInfo?.id) return;
      setApproveDrafts((current) => ({ ...current, [approveDrawerInfo.id]: draft }));
    },
    [approveDrawerInfo?.id],
  );
  const handleCloseApproveDrawer = useCallback(
    ({ resetDraft = false }: { resetDraft?: boolean } = {}) => {
      const requestId = approveDrawerInfo?.id;
      setApproveDrawerInfo(null);
      if (resetDraft && requestId) {
        setApproveDrafts((current) => {
          const next = { ...current };
          delete next[requestId];
          return next;
        });
      }
    },
    [approveDrawerInfo?.id],
  );
  const handleApproveTerminalService = useCallback(
    ({
      jobType,
      operatorName,
      notesToCustomer,
      bookingConfirmation,
    }: {
      jobType: string;
      operatorName?: string;
      notesToCustomer?: string;
      bookingConfirmation: TerminalServiceDocument;
    }) => {
      if (!approveDrawerInfo) return;
      approveTerminalServiceMutation.mutate({
        id: approveDrawerInfo.id,
        serviceType: approveDrawerInfo.serviceType,
        payload: {
          status: FottServiceRequestStatus.APPROVED,
          jobType,
          operatorName,
          approvalNotes: notesToCustomer,
          bookingConfirmation,
          createdBy: user?.name ?? user?.email ?? user?.preferredUsername ?? "",
          createdByRole: user?.roles?.[0] ?? "",
          createdAt: new Date().toISOString(),
        },
      });
    },
    [approveDrawerInfo, approveTerminalServiceMutation, user],
  );

  const handleCancelTerminalService = useCallback(
    ({
      cancellationReason,
      attachment,
    }: {
      cancellationReason: string;
      attachment: TerminalServiceDocument | null;
    }) => {
      if (!cancelDrawerInfo) return;

      cancelTerminalServiceMutation.mutate({
        id: cancelDrawerInfo.id,
        serviceType: cancelDrawerInfo.serviceType,
        payload: {
          reason: cancellationReason,
          attachment,
        },
      });
    },
    [cancelDrawerInfo, cancelTerminalServiceMutation, user],
  );

  const handleCloseBypassService = useCallback(
    (payload: CloseBypassServiceRequestPayload) => {
      if (!closeDrawerInfo) {
        return;
      }

      closeBypassServiceMutation.mutate({
        id: closeDrawerInfo.id,
        payload,
      });
    },
    [closeBypassServiceMutation, closeDrawerInfo],
  );

  const handleSubmitUsage = useCallback(
    (values: UsageDrawerSubmitValues) => {
      if (!usageDrawerInfo) {
        return;
      }

      submitUsageMutation.mutate({
        id: usageDrawerInfo.id,
        serviceType: usageDrawerInfo.serviceType,
        payload: {
          startTime: toUtcDateTime(values.startTime),
          endTime: toUtcDateTime(values.endTime),
          ...(values.pressureInBar !== undefined ? { pressureInBar: values.pressureInBar } : {}),
        },
      });
    },
    [submitUsageMutation, usageDrawerInfo],
  );

  const headers: Header[] = useMemo(() => {
    const fottServicesHeaders: Header[] = [
      {
        field: FOTT_SERVICES_COLUMNS.ID,
        columnName: t(`${LANG_BASE_HEADER}.serviceId`),
      },
      {
        field: FOTT_SERVICES_COLUMNS.TYPE,
        columnName: t(`${LANG_BASE_HEADER}.serviceType`),
      },
      {
        field: FOTT_SERVICES_COLUMNS.BOOKING_REQUESTED_FROM,
        columnName: t(`${LANG_BASE_HEADER}.bookingRequestedFrom`),
      },
      {
        field: FOTT_SERVICES_COLUMNS.BOOKING_REQUESTED_TO,
        columnName: t(`${LANG_BASE_HEADER}.bookingRequestedTo`),
      },
      {
        field: FOTT_SERVICES_COLUMNS.ON_BEHALF,
        columnName: t(`${LANG_BASE_HEADER}.onBehalf`),
      },
      {
        field: FOTT_SERVICES_COLUMNS.CHARGEABLE_PARTY,
        columnName: t(`${LANG_BASE_HEADER}.chargeableParty`),
      },
      {
        field: FOTT_SERVICES_COLUMNS.NO_OF_USAGE_HOURS,
        columnName: t(`${LANG_BASE_HEADER}.noOfUsageHours`),
      },
      {
        field: FOTT_SERVICES_COLUMNS.STATUS,
        columnName: t(`${LANG_BASE_HEADER}.status`),
      },
    ];

    if (shouldShowFottActions) {
      fottServicesHeaders.push({
        field: FOTT_SERVICES_COLUMNS.ACTIONS,
        columnName: t(`${LANG_BASE_HEADER}.actions`),
        width: 96,
        minWidth: 96,
        flex: 0,
      });
    }

    return fottServicesHeaders;
  }, [shouldShowFottActions, t]);

  const filterFields = useMemo<Record<string, FilterFieldDefinition>>(
    () => buildFottServicesFilterFields(t, companyOptions),
    [t, companyOptions],
  );
  const formattedFormData = useMemo<FottServicesGridRow[]>(
    () =>
      (data?.items ?? []).map((item) => {
        const status = optimisticRenderedStatus[item.id] ?? item.status ?? "";
        const actionAvailability = getTerminalServiceFlowActionAvailability({
          taskStatus: item.taskStatus,
          claimOwnerName: item.taskClaimOwnerName,
          status,
        });
        const hasNitrogenUsage =
          item.serviceType === FottServiceType.NITROGEN_SUPPLY_SERVICE &&
          typeof item.numberOfUsageHours === "number" &&
          item.numberOfUsageHours > 0;

        return {
          id: item.id,
          detailsNavigationParam: buildDetailsNavigationParam(item.id, item.serviceType),
          fottServiceType: item.serviceType,
          taskId: item.taskId,
          taskStatus: item.taskStatus,
          taskClaimOwnerName: item.taskClaimOwnerName,
          [FOTT_SERVICES_COLUMNS.ID]: item.serviceId,
          rawStatus: status,
          badgeType: getTerminalServiceStatusBadgeType(status),
          [FOTT_SERVICES_COLUMNS.TYPE]: t(`${LANG_BASE}.service-type.${item.serviceType ?? ""}`),
          [FOTT_SERVICES_COLUMNS.BOOKING_REQUESTED_FROM]: item.bookingRequestedFrom
            ? formatDateToDDMMYYYY(convertUtcToUae(item.bookingRequestedFrom), true)
            : "-",
          [FOTT_SERVICES_COLUMNS.BOOKING_REQUESTED_TO]: item.bookingRequestedTo
            ? formatDateToDDMMYYYY(convertUtcToUae(item.bookingRequestedTo), true)
            : "-",
          [FOTT_SERVICES_COLUMNS.ON_BEHALF]: item.onBehalf,
          [FOTT_SERVICES_COLUMNS.CHARGEABLE_PARTY]: item.chargeableParty,
          [FOTT_SERVICES_COLUMNS.NO_OF_USAGE_HOURS]: item.numberOfUsageHours,
          [FOTT_SERVICES_COLUMNS.STATUS]: t(`${LANG_BASE}.status.${status}`),
          ...(shouldShowFottActions
            ? {
                [FOTT_SERVICES_COLUMNS.ACTIONS]: (
                  <FottServicesActionsCell
                    id={item.id}
                    serviceType={item.serviceType}
                    isClaiming={claimTerminalServiceMutation.isPending}
                    isUnclaiming={unclaimTerminalServiceMutation.isPending}
                    canAddUsage={
                      status === FottServiceRequestStatus.APPROVED &&
                      item.serviceType !== FottServiceType.BYPASS_SERVICE &&
                      !hasNitrogenUsage
                    }
                    canCancel={canCancelTerminalService({
                      status,
                      serviceDate: item.bookingRequestedFrom,
                      serviceType: item.serviceType,
                      numberOfUsageHours: item.numberOfUsageHours,
                    })}
                    actionAvailability={actionAvailability}
                    labels={{
                      claim: t(`${LANG_BASE}.actions.claim`),
                      unclaim: t(`${LANG_BASE}.actions.unclaim`),
                      approve: t(`${LANG_BASE}.actions.approve`),
                      reject: t(`${LANG_BASE}.actions.reject`),
                      addUsage: t(`${LANG_BASE}.actions.add-usage`, {
                        defaultValue: "Add Usage",
                      }),
                      cancel: t(`${LANG_BASE}.actions.cancel`, { defaultValue: "Cancel" }),
                      viewCustomers: t(`${LANG_BASE}.actions.view-customers`),
                      downloadPlda: t(`${LANG_BASE}.actions.download-plda`),
                    }}
                    onClaim={handleClaimTerminalService}
                    onUnclaim={handleUnclaimTerminalService}
                    onApprove={handleOpenApproveDrawer}
                    onReject={handleOpenRejectDrawer}
                    onAddUsage={handleOpenUsageDrawer}
                    onCancelService={handleOpenCancelDrawer}
                  />
                ),
              }
            : {}),
        };
      }),
    [
      claimTerminalServiceMutation.isPending,
      data?.items,
      handleClaimTerminalService,
      handleOpenApproveDrawer,
      handleOpenRejectDrawer,
      handleOpenUsageDrawer,
      handleOpenCancelDrawer,
      handleUnclaimTerminalService,
      optimisticRenderedStatus,
      shouldShowFottActions,
      t,
      unclaimTerminalServiceMutation.isPending,
    ],
  );

  const onApplyFilters = (newFilters: FilterItem[]) => {
    setFilters(newFilters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(newFilters));

    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const isLoading = isFottServicesLoading || isLoadingCompanies;
  const usageDrawerServiceDetails = usageDrawerDetails?.serviceDetails as
    | {
        usageHours?: TerminalServiceUsageEntry[] | number | null;
        usageHoursFott?: TerminalServiceUsageEntry[] | null;
      }
    | null
    | undefined;
  const usageDrawerUsageHours =
    usageDrawerInfo?.serviceType === FottServiceType.NITROGEN_SUPPLY_SERVICE
      ? usageDrawerServiceDetails?.usageHoursFott
      : usageDrawerServiceDetails?.usageHours;
  const usageDrawerEntries = Array.isArray(usageDrawerUsageHours) ? usageDrawerUsageHours : [];

  if (error) {
    return <Snackbar open autoHideDuration={6000} message={(error as Error).message} />;
  }

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      <CustomDataTable
        testId={TEST_ID_BASE}
        title={t(`${LANG_BASE}.title`)}
        actionName={canRequestFottService ? t(`${LANG_BASE}.request-new-service`) : ""}
        headers={headers}
        rows={formattedFormData}
        page={page}
        pageSize={pageSize}
        totalCount={data?.totalElements ?? 0}
        canPreview={true}
        onPageChange={(newPage, newSize) => {
          setPage(newPage);
          setPageSize(newSize);
          localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
          localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
        }}
        navigationLinkBase={paths.terminalServices.terminalServiceDetails}
        navigationParam="detailsNavigationParam"
        pageSizeOptions={[5, 10, 15, 20, 25]}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        availableFields={filterFields}
        actionColumn={
          isCustomer
            ? {
                actionTitle: t("terminal-services.details.close-button"),
                onActionClick: (row) => {
                  setCloseDrawerInfo({
                    id: row.id,
                    serviceType: row.fottServiceType,
                  });
                },
                isActionEnabled: (row) =>
                  row.fottServiceType === FottServiceType.BYPASS_SERVICE &&
                  row.rawStatus === FottServiceRequestStatus.APPROVED,
              }
            : undefined
        }
      />
      <RejectTerminalServiceDrawer
        key={`reject-${rejectDrawerInfo?.id ?? "closed"}-${rejectDrawerResetVersion}`}
        isOpen={Boolean(rejectDrawerInfo)}
        requestId={rejectDrawerInfo?.id}
        draft={
          rejectDrawerInfo?.id && rejectDrafts[rejectDrawerInfo.id]
            ? rejectDrafts[rejectDrawerInfo.id]
            : { notesToCustomer: "" }
        }
        onDraftChange={handleRejectDraftChange}
        onClose={handleCloseRejectDrawer}
        onReject={handleRejectTerminalService}
        isSubmitting={rejectTerminalServiceMutation.isPending}
      />
      <ApproveTerminalServiceDrawer
        key={`approve-${approveDrawerInfo?.id ?? "closed"}-${approveDrawerResetVersion}`}
        isOpen={Boolean(approveDrawerInfo)}
        requestId={approveDrawerInfo?.id}
        serviceType={approveDrawerInfo?.serviceType ?? FottServiceType.GANTRY_CRANE_SERVICE}
        draft={
          approveDrawerInfo?.id && approveDrafts[approveDrawerInfo.id]
            ? approveDrafts[approveDrawerInfo.id]
            : { jobType: "", operatorName: "", notesToCustomer: "" }
        }
        onDraftChange={handleApproveDraftChange}
        onClose={handleCloseApproveDrawer}
        onApprove={handleApproveTerminalService}
        isSubmitting={approveTerminalServiceMutation.isPending}
      />
      <CloseBypassServiceDrawer
        key={`close-${closeDrawerInfo?.id ?? "closed"}-${closeDrawerResetVersion}`}
        isOpen={Boolean(closeDrawerInfo)}
        requestId={closeDrawerInfo?.id}
        onClose={() => setCloseDrawerInfo(null)}
        onCloseService={handleCloseBypassService}
        isSubmitting={closeBypassServiceMutation.isPending}
      />
      {cancelDrawerInfo && (
        <CancelTerminalServiceDrawer
          key={`cancel-${cancelDrawerInfo.id}-${cancelDrawerResetVersion}`}
          isOpen={Boolean(cancelDrawerInfo)}
          requestId={cancelDrawerInfo.id}
          draft={
            cancelDrafts[cancelDrawerInfo.id]
              ? cancelDrafts[cancelDrawerInfo.id]
              : { cancellationReason: "" }
          }
          onDraftChange={handleCancelDraftChange}
          onClose={handleCloseCancelDrawer}
          onCancelService={handleCancelTerminalService}
          isSubmitting={cancelTerminalServiceMutation.isPending}
        />
      )}
      <UsageDrawer
        isOpen={Boolean(usageDrawerInfo)}
        mode="add"
        entries={usageDrawerEntries}
        serviceType={usageDrawerInfo?.serviceType}
        testIdBase={`${TEST_ID_BASE}-usage`}
        isSubmitting={submitUsageMutation.isPending}
        onClose={() => setUsageDrawerInfo(null)}
        onSubmit={handleSubmitUsage}
      />
    </div>
  );
};

export default FottServicesGrid;
